import type { ReactElement } from 'react';
import { forwardRef } from 'react';
import Tippy from '@tippyjs/react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { ZINDEX } from '@utils/constants';

import type { Placement } from 'tippy.js';

export interface TooltipProps {
  /**
   * Text to be shown in the tooltip. Only string is accepted. For interactive tooltips check the Popover component.
   */
  text: string;
  /**
   * Disable the tooltip entirely.
   */
  isDisabled?: boolean;
  /**
   * Define rule for preferred placement.
   */
  placement?: Placement;
  /**
   * Max width of the tooltip in pixels. Capped at 320.
   */
  maxWidth?: number;
  /**
   * If the tooltip hides upon clicking the reference or outside of the tooltip.
   * `toggle` will hide only upon clicking the reference, but not outside.
   */
  hideOnClick?: boolean | 'toggle';
  /**
   * z-index of the Tooltip
   */
  zIndex?: number;
  /**
   * Define rule for manual visibility
   */
  visible?: boolean;
  /**
   * The element to append the tooltip to. Sometimes the tooltip needs to be appended to a different DOM context due to accessibility, clipping, or z-index issues.
   * It should only be used when needed. By default the tooltip is appended to the document root.
   */
  appendTo?: HTMLElement | 'parent' | ((ref: Element) => Element);
  /**
   * The component already takes into account the boundaries of any clipping parent.
   * If you need to respect the boundaries of a non-clipping component, you can specify it here.
   */
  boundary?: HTMLElement | null;
  /**
   * Show tooltip as soon as it's created,
   * not just when there's a hover event.
   */
  showOnCreate?: boolean;
  /**
   * Tippy lifecycle hook, here it is used to autohide
   * the tooltip when we are showing it through showOnCreate
   */
  onShow?: (instance: { hide: () => void }) => void | false;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Children prop from React.
   */
  children: ReactElement;
}

const StyledTippy = styled(Tippy)`
  position: relative;
  outline: 0;
  transition-property: transform, visibility, opacity;
  ${tokens.text('body2')};
  color: ${tokens.color.text_default_inverse};
  background-color: ${tokens.BRAND_COLORS.neutral_950};
  border-radius: ${tokens.radius.m};
  ${tokens.elevation(2)};
  padding: ${tokens.spacer.x1};

  &[data-animation='fade'][data-state='hidden'] {
    opacity: 0;
  }

  &[data-inertia][data-state='visible'] {
    transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
  }

  .tippy-content {
    position: relative;
    z-index: 1;
    padding: 0;
    white-space: pre-line;
    word-break: normal;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .tippy-arrow {
    width: 16px;
    height: 16px;
    color: ${tokens.BRAND_COLORS.neutral_950};
  }
  .tippy-arrow:before {
    content: '';
    position: absolute;
    border-color: transparent;
    border-style: solid;
  }

  &[data-placement^='top'] > .tippy-arrow {
    bottom: 0;
  }
  &[data-placement^='top'] > .tippy-arrow:before {
    bottom: -7px;
    left: 0;
    border-width: 8px 8px 0;
    border-top-color: initial;
    transform-origin: center top;
  }
  &[data-placement^='bottom'] > .tippy-arrow {
    top: 0;
  }
  &[data-placement^='bottom'] > .tippy-arrow:before {
    top: -7px;
    left: 0;
    border-width: 0 8px 8px;
    border-bottom-color: initial;
    transform-origin: center bottom;
  }
  &[data-placement^='left'] > .tippy-arrow {
    right: 0;
  }
  &[data-placement^='left'] > .tippy-arrow:before {
    border-width: 8px 0 8px 8px;
    border-left-color: initial;
    right: -7px;
    transform-origin: center left;
  }
  &[data-placement^='right'] > .tippy-arrow {
    left: 0;
  }
  &[data-placement^='right'] > .tippy-arrow:before {
    left: -7px;
    border-width: 8px 8px 8px 0;
    border-right-color: initial;
    transform-origin: center right;
  }
`;

/**
 * A tooltip is a floating, non-actionable label used to display contextual information about the element they are paired with.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/605b37-tooltip/b/31bb2a Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-tooltip--basic-tooltip Code documentation}
 *
 * @example
 * <Tooltip text="My tooltip" children={<span>Element</span>}/>
 */
export const Tooltip = forwardRef<HTMLElement, TooltipProps>(
  (
    {
      children,
      text,
      isDisabled = false,
      placement = 'top',
      maxWidth = 320,
      appendTo = () => document.body,
      hideOnClick = true,
      zIndex = ZINDEX.TOOLTIP,
      showOnCreate = false,
      onShow = () => null,
      boundary,
      visible,
      className,
    }: TooltipProps,
    ref,
  ) => {
    return (
      <StyledTippy
        visible={visible}
        content={<span>{text}</span>}
        disabled={isDisabled}
        placement={placement}
        className={className}
        maxWidth={maxWidth > 320 ? 320 : maxWidth}
        ref={ref}
        appendTo={appendTo}
        hideOnClick={hideOnClick}
        zIndex={zIndex}
        showOnCreate={showOnCreate}
        onShow={onShow}
        popperOptions={
          boundary
            ? {
                modifiers: [
                  { name: 'preventOverflow', options: { boundary } },
                  {
                    name: 'flip',
                    options: {
                      boundary,
                      fallbackPlacements: ['right', 'left', 'top', 'bottom'],
                    },
                  },
                ],
              }
            : undefined
        }
      >
        {children}
      </StyledTippy>
    );
  },
);

Tooltip.displayName = 'Tooltip';
